/**
 * 埋点初始化管理器
 * 解决用户信息获取和自动埋点的时序问题
 *
 * 在初始化前缓存事件，初始化后自动启用页面/错误/点击追踪
 */

import {
  slsLogger,
  enableAutoPageTracking,
  enableAutoErrorTracking,
  enableAutoClickTracking,
  generatePvKey,
} from "./slsLogger"

class Tracker {
  private isInitialized = false
  private pendingEvents: Array<() => void> = []

  /**
   * 初始化并监听用户状态变化
   * @param getUserId 获取当前用户 ID 的函数
   * @param subscribe 订阅用户 ID 变化的函数，返回取消订阅函数
   */
  async initializeWithSubscription(
    getUserId: () => string | undefined,
    subscribe: (callback: (userId: string | undefined) => void) => () => void,
  ) {
    // 等待用户信息
    await this.waitForUserId(getUserId)

    const userId = getUserId()
    if (userId) {
      slsLogger.setUserId(userId)
    }

    enableAutoPageTracking()
    enableAutoErrorTracking()
    enableAutoClickTracking()

    this.processPendingEvents()
    this.isInitialized = true

    // 监听用户状态变化；store 的任何字段更新都会触发回调，只在用户 ID 变化时处理
    let currentUserId = userId
    subscribe((newUserId) => {
      if (newUserId === currentUserId) return
      currentUserId = newUserId

      slsLogger.setUserId(newUserId ?? "")

      if (newUserId) {
        // 重新追踪当前页面，携带新的用户信息
        slsLogger.trackPage(
          window.location.pathname + window.location.search,
          generatePvKey(),
          { reason: "user_login" },
        )
      }
    })
  }

  /** 等待用户 ID 可用 */
  private async waitForUserId(
    getUserId: () => string | undefined,
    maxWaitTime = 3000,
  ) {
    const startTime = Date.now()
    while (!getUserId() && Date.now() - startTime < maxWaitTime) {
      // 轮询等待，必须串行
      // oxlint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 100))
    }
  }

  /**
   * 手动追踪行为事件
   * 在初始化前会缓存，初始化后立即发送
   */
  action(
    action: string,
    category: string,
    data: Record<string, unknown> = {},
    pvKey?: string,
  ) {
    if (this.isInitialized) {
      slsLogger.trackAction(action, category, data, pvKey)
    } else {
      this.pendingEvents.push(() =>
        slsLogger.trackAction(action, category, data, pvKey),
      )
    }
  }

  /** 处理等待中的事件 */
  private processPendingEvents() {
    while (this.pendingEvents.length > 0) {
      const event = this.pendingEvents.shift()
      event?.()
    }
  }
}

export const tracker = new Tracker()
